import {
    ASSET_NAME_BOND,
    ASSET_NAME_STOCK,
    ASSET_NAME_CURRENCY,
    ASSET_NAME_FOND,
    ASSET_NAME_INDEX,
    FIELD_ISIN,
    FIELD_TICKER,
    SORT_ASC,
    SORT_DESC,
    SORTING_PROFITABILITY,
    SORTING_DYNAMICS,
    CODE_ALL_ASSET_GROUPS,
    PROFITABILITY_TYPE_YTM,
} from '../constants';
import { sortableFields } from '../config';
import type {
    IAsset,
    IAssetGroup,
    TAssetTypeCode,
    IIssuer,
    ISortableField,
    TAssetGroups,
} from '../models';

// TODO: это больше подходит на перенос в invest-common/numbers-helper
/** Проверяет является ли переданное значение числом */
export const isNumber = (value: unknown): value is number => typeof value === 'number';

export const isStock = (assetType: TAssetTypeCode) => assetType === ASSET_NAME_STOCK;
export const isBond = (assetType: TAssetTypeCode) => assetType === ASSET_NAME_BOND;
export const isCurrency = (assetType: TAssetTypeCode) => assetType === ASSET_NAME_CURRENCY;
export const isFond = (assetType: TAssetTypeCode) => assetType === ASSET_NAME_FOND;
export const isIndex = (assetType: TAssetTypeCode) => assetType === ASSET_NAME_INDEX;

type TFilterType = typeof FIELD_ISIN | typeof FIELD_TICKER;
const mapFilterType: Record<TAssetTypeCode, TFilterType> = {
    [ASSET_NAME_BOND]: FIELD_ISIN,
    [ASSET_NAME_CURRENCY]: FIELD_ISIN,
    [ASSET_NAME_FOND]: FIELD_ISIN,
    [ASSET_NAME_INDEX]: FIELD_ISIN,
    [ASSET_NAME_STOCK]: FIELD_TICKER,
};
/** Получает тип фильтра. Для акций - тикер, для облигаций, валюты и фондов - ISIN */
export const getFilterType = (assetType: TAssetTypeCode) => mapFilterType[assetType];

interface IGetSymbolForPrice {
    assetType: TAssetTypeCode;
    symbol?: string | null;
}

/** Определяет символ для цены. Для акции - символ валюты, для облигаций - символ процента */
export const getSymbolForPrice = ({ assetType, symbol }: IGetSymbolForPrice) =>
    isBond(assetType) ? '%' : symbol ?? '';

/** Превращает 1 букву строки в заглавную */
export const setFirstCharInUpperCase = (str: string) => {
    const strInLowerCase = str.toLowerCase().trim();

    return strInLowerCase.charAt(0).toUpperCase() + strInLowerCase.slice(1);
};

/** Очищает строку от лишних символов и возвращает 1 символ в заглавной форме */
export const getFirstSymbolFromIssuerName = (str?: string) =>
    str?.replace(/"|'/g, '').trim().slice(0, 1).toUpperCase() ?? '';

/** Возвращает для акции ticker, для облигации - ISIN */
export const getTickerOrIsin = <T extends Pick<IAsset, 'assetType' | 'ISIN' | 'ticker'>>(
    active: T
) => {
    const { assetType } = active;
    const { code } = assetType;

    const tickerOrIsin = active[mapFilterType[code]];

    return tickerOrIsin.toUpperCase();
};

// TODO нужно три хелпера с дженериками перенести в invest-common: используется в emitents и в mediaportal
/** Получает имя эмитента */
export const getIssuerName = <T extends Pick<IIssuer, 'name' | 'shortName'>>(issuer: T) =>
    issuer.shortName || issuer.name;

/** Получает имя ассета */
export const getAssetName = <T extends Pick<IAsset, 'name'>>(asset: T) => asset.name;

const assetMapToGetName: Record<
    TAssetTypeCode,
    <T extends Pick<IAsset, 'issuer' | 'name'>>(asset: T) => string
> = {
    [ASSET_NAME_STOCK]: asset => getIssuerName(asset.issuer),
    [ASSET_NAME_BOND]: asset => getIssuerName(asset.issuer),
    [ASSET_NAME_CURRENCY]: asset => getAssetName(asset),
    [ASSET_NAME_FOND]: asset => getAssetName(asset),
    [ASSET_NAME_INDEX]: asset => getAssetName(asset),
};

/** Получает имя для актива в зависимости от его типа */
export const getAssetNameByType = <T extends Pick<IAsset, 'issuer' | 'name'>>(
    code: TAssetTypeCode,
    asset: T
) => assetMapToGetName[code](asset);

/** Возвращает модуль числа */
export const getMathAbs = (num?: number | null) => Math.abs(num ?? 0);

/** Формирует уникальное имя для SSR инстанса */
export const getSSRInstanceName = (name: string, id: string) => `${name}_${id}`;

/** Возвращает порядок сортировки */
export const getSortDirection = (value: string) => (value === SORT_ASC ? SORT_DESC : SORT_ASC);

type TProfitabilityLabel = typeof SORTING_PROFITABILITY | typeof SORTING_DYNAMICS;

const mapAssetTypeToProfitabilityLabel: Record<TAssetTypeCode, TProfitabilityLabel> = {
    [ASSET_NAME_BOND]: SORTING_PROFITABILITY,
    [ASSET_NAME_STOCK]: SORTING_PROFITABILITY,
    [ASSET_NAME_CURRENCY]: SORTING_DYNAMICS,
    [ASSET_NAME_FOND]: SORTING_DYNAMICS,
    [ASSET_NAME_INDEX]: SORTING_DYNAMICS,
};

/** Получает список полей для сортировки в зависимости от типа ассета **/
export const getFieldsForSortByAssetType = (assetType: TAssetTypeCode): ISortableField[] => {
    const byName = { value: sortableFields.shortName, label: 'По названию' };
    const byPrice = { value: sortableFields.lastPrice, label: 'По цене' };
    const byProfitability = {
        value: `${sortableFields.profitabilityPercent}.${PROFITABILITY_TYPE_YTM}`,
        label: mapAssetTypeToProfitabilityLabel[assetType],
    };

    if (!isBond(assetType)) {
        return [byName, byPrice];
    }

    return [byName, byProfitability, byPrice];
};

/** Проверяет передали ли какую-то группу активов */
export const isAssetGroupWasPassed = (value: string) => value !== CODE_ALL_ASSET_GROUPS;

// TODO: Удалить, как на бэке появятся приоритеты групп
// на бэке их пока нет, приходится на фронте делать
/** Приоритеты для групп */
export const assetGroupsPriority: Record<TAssetGroups, number> = {
    IMOEX: 1,
    MOEXBC: 10,
};

/** Сортирует группы ассетов согласно их приоритету */
export const sortAssetGroupsByPriority = (assetGroups: IAssetGroup[]) =>
    assetGroups
        .map(group => ({
            ...group,
            priority: assetGroupsPriority[group.code as TAssetGroups] ?? 0,
        }))
        .sort((a, b) => b.priority - a.priority);

/** Формирует лейбл для таба в формате "Акции 10" */
// eslint-disable-next-line no-irregular-whitespace
export const getLabelAssetTypeWithCount = (label: string, count: number) => `${label} ${count}`;
